import { supabase } from './supabase'

// Get user's IP address (client-side approximation)
const getClientInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language
  }
}

// Log activity
export const logActivity = async (userId, action, details = '', metadata = {}) => {
  try {
    const clientInfo = getClientInfo()
    
    const { error } = await supabase
      .from('activity_logs')
      .insert({
        user_id: userId,
        action,
        details,
        metadata: {
          ...metadata,
          ...clientInfo
        }
      })

    if (error) throw error
    
    return { error: null }
  } catch (error) {
    console.error('Failed to log activity:', error)
    return { error: error.message }
  }
}

// Get user activities
export const getUserActivities = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('activity_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Get all activities (admin only)
export const getAllActivities = async (filters = {}, limit = 100) => {
  try {
    let query = supabase
      .from('activity_logs')
      .select(`
        *,
        users (username, email, full_name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit)

    // Apply filters
    if (filters.userId) {
      query = query.eq('user_id', filters.userId)
    }
    if (filters.action) {
      query = query.eq('action', filters.action)
    }
    if (filters.startDate) {
      query = query.gte('created_at', filters.startDate)
    }
    if (filters.endDate) {
      query = query.lte('created_at', filters.endDate)
    }

    const { data, error } = await query

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Get activity statistics
export const getActivityStats = async (userId = null, days = 30) => {
  try {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    let query = supabase
      .from('activity_logs')
      .select('action, created_at')
      .gte('created_at', startDate.toISOString())

    if (userId) {
      query = query.eq('user_id', userId)
    }

    const { data, error } = await query

    if (error) throw error

    // Group by action
    const stats = data.reduce((acc, log) => {
      acc[log.action] = (acc[log.action] || 0) + 1
      return acc
    }, {})

    return { data: stats, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Search activities
export const searchActivities = async (searchTerm, limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('activity_logs')
      .select(`
        *,
        users (username, email, full_name)
      `)
      .or(`action.ilike.%${searchTerm}%,details.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Delete old activities (cleanup - admin only)
export const deleteOldActivities = async (daysToKeep = 90) => {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep)

    const { error } = await supabase
      .from('activity_logs')
      .delete()
      .lt('created_at', cutoffDate.toISOString())

    if (error) throw error

    return { error: null }
  } catch (error) {
    return { error: error.message }
  }
}

export default {
  logActivity,
  getUserActivities,
  getAllActivities,
  getActivityStats,
  searchActivities,
  deleteOldActivities
}